import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { adminApi } from '../services/api'
import { useAuth } from './AuthContext'

// Tipuri pentru setările aplicației
export interface ApplicationSettings {
  app_name: string
  app_description: string
  app_version: string
  app_company: string
  app_website?: string
  app_support_email?: string
  app_logo_url?: string
  app_favicon_url?: string
  app_theme_primary_color: string
  app_theme_secondary_color: string
  app_theme_mode: 'light' | 'dark' | 'auto'
  app_language: string
  app_timezone: string
  app_date_format: string
  app_time_format: string
  app_currency: string
  app_currency_symbol: string
  // Domain configuration (stored in domain_config table)
  primary_domain?: string
  ssl_mode?: 'staging' | 'production'
}

// Context pentru setările aplicației
interface AppSettingsContextType {
  settings: ApplicationSettings | null
  loading: boolean
  error: string | null
  refreshSettings: () => Promise<void>
  updateSettings: (newSettings: Partial<ApplicationSettings>) => Promise<void>
}

const AppSettingsContext = createContext<AppSettingsContextType | undefined>(undefined)

// Provider pentru setările aplicației
export const AppSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const [settings, setSettings] = useState<ApplicationSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Funcția pentru încărcarea setărilor publice (fără autentificare)
  const loadPublicSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const baseUrl = `${window.location.protocol}//${window.location.host}`
      const response = await fetch(`${baseUrl}/api/v1/public/app-info`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const publicInfo = await response.json()
      
      // Convertim informațiile publice în format ApplicationSettings (DOAR info publică)
      setSettings({
        app_name: publicInfo.app_name,
        app_description: publicInfo.app_description,
        app_version: publicInfo.app_version,
        app_company: '', // Va fi setat din setările complete după autentificare
        app_theme_primary_color: '#1976d2',
        app_theme_secondary_color: '#dc004e',
        app_theme_mode: 'auto',
        app_language: 'ro',
        app_timezone: 'Europe/Bucharest',
        app_date_format: 'DD.MM.YYYY',
        app_time_format: 'HH:mm',
        app_currency: 'RON',
        app_currency_symbol: 'Lei'
      })
    } catch (error: any) {
      console.log('Info: Folosesc setările default pentru public:', error.message)
      
      // Fallback doar în caz extrem - NU se folosesc valori hardcoded
      setSettings(null) // Forțez să folosească doar datele publice
      setError(null)
    } finally {
      setLoading(false)
    }
  }

  // Funcția pentru încărcarea setărilor complete (cu autentificare)
  const loadAuthenticatedSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await adminApi.getAppSettings()
      setSettings(response)
    } catch (error: any) {
      console.log('Info: Eroare la încărcarea setărilor autentificate:', error.response?.status)
      // În caz de eroare, păstrăm setările publice
      setError('Nu s-au putut încărca setările complete')
    } finally {
      setLoading(false)
    }
  }

  // Funcția pentru actualizarea setărilor
  const updateSettings = async (newSettings: Partial<ApplicationSettings>) => {
    try {
      setError(null)
      
      // Actualizează setările locale
      if (settings) {
        const updatedSettings = { ...settings, ...newSettings }
        setSettings(updatedSettings)
      }
      
      // Salvează în backend
      await adminApi.updateSystemSettings({ application: newSettings })
      
    } catch (err) {
      console.error('Eroare la actualizarea setărilor:', err)
      setError('Nu s-au putut actualiza setările')
      throw err // Re-throw pentru a permite gestionarea erorii în componenta care apelează
    }
  }

  // Funcția pentru reîncărcarea setărilor
  const refreshSettings = async () => {
    if (user) {
      await loadAuthenticatedSettings()
    } else {
      await loadPublicSettings()
    }
  }

  // Încarcă setările DOAR pentru utilizatori autentificați
  useEffect(() => {
    if (user) {
      // Utilizator autentificat - încarcă setările complete
      loadAuthenticatedSettings()
    }
    // Pentru utilizatori neautentificați NU se încarcă nimic aici
    // Pagina de login folosește usePublicAppInfo direct
  }, [user]) // Depinde de starea de autentificare

  const value: AppSettingsContextType = {
    settings,
    loading,
    error,
    refreshSettings,
    updateSettings
  }

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  )
}

// Hook pentru utilizarea contextului
export const useAppSettings = (): AppSettingsContextType => {
  const context = useContext(AppSettingsContext)
  if (context === undefined) {
    throw new Error('useAppSettings trebuie folosit în AppSettingsProvider')
  }
  return context
}

export default AppSettingsContext
